import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setApiChurchId } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';

const AuthContext = createContext(null);

const CHURCH_KEY = 'suite_selected_church';

/** El admin global arranca en "Todas las iglesias" y su elección se recuerda entre recargas. */
const initialChurch = (u) => {
  if (!u?.is_superadmin) return u?.church_id || null;
  return localStorage.getItem(CHURCH_KEY) || null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChurchId, setSelectedChurchId] = useState(null);

  useEffect(() => {
    secureStorage.migrateFromLocalStorage();

    const token = secureStorage.getToken();
    const savedUser = secureStorage.getUser();
    if (token && savedUser) {
      setUser(savedUser);
      setSelectedChurchId(initialChurch(savedUser));
      setPermissions(savedUser.permissions || {});
      // Re-sync with the Hub (permissions may have changed in the app)
      authAPI.getMe().then(res => {
        const fresh = res.data?.user;
        if (fresh) {
          secureStorage.setUser(fresh);
          setUser(fresh);
          setPermissions(fresh.permissions || {});
          if (!fresh.is_superadmin) setSelectedChurchId(fresh.church_id || null);
        }
      }).catch(() => {
        secureStorage.clear();
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier, password) => {
    const res = await authAPI.login(identifier, password);
    const { jwt, user: userData } = res.data;
    secureStorage.setToken(jwt);
    secureStorage.setUser(userData);
    setUser(userData);
    setPermissions(userData.permissions || {});
    setSelectedChurchId(initialChurch(userData));
    return userData;
  }, []);

  const logout = useCallback(() => {
    authAPI.logout().catch(() => {});
    secureStorage.clear();
    localStorage.removeItem(CHURCH_KEY);
    setUser(null);
    setPermissions({});
    setSelectedChurchId(null);
  }, []);

  const switchChurch = useCallback((churchId) => {
    if (churchId) localStorage.setItem(CHURCH_KEY, churchId);
    else localStorage.removeItem(CHURCH_KEY);
    setSelectedChurchId(churchId);
  }, []);

  const hasPermission = useCallback((module, action) => {
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [permissions]);

  const canRead = useCallback((module) => hasPermission(module, 'read'), [hasPermission]);
  const canCreate = useCallback((module) => hasPermission(module, 'create'), [hasPermission]);
  const canUpdate = useCallback((module) => hasPermission(module, 'update'), [hasPermission]);
  const canDelete = useCallback((module) => hasPermission(module, 'delete'), [hasPermission]);

  const roleType = user?.role?.type || 'member';

  // Se aplica en el render (no en un efecto) para que el primer fetch de cada página ya lleve
  // la congregación en contexto.
  setApiChurchId(selectedChurchId);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      permissions,
      hubPermissions: user?.hub_permissions || [],
      hasPermission,
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      selectedChurchId,
      switchChurch,
      isAuthenticated: !!user,
      isSuperAdmin: user?.is_superadmin === true,
      isPastor: roleType === 'pastor',
      isCollaborator: roleType === 'member',
      canManageTeam: user?.can_manage_team === true || user?.is_superadmin === true || roleType === 'pastor',
      roleType,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
